import type {
  AllergenDefinition,
  Category,
  Department,
  IdentityAdministration,
  ProductAllergen,
  ProductDetail,
  ReferenceData,
  SessionInfo,
  ShelfLifeRule,
  StorageState,
  UnitOfMeasure,
} from "./types";
import catalogue from "./windmill-catalogue.generated.json";

export const DEMO_ORGANIZATION_ID = "11111111-1111-4111-8111-111111111111";

/**
 * The catalogue below is generated by `scripts/build_windmill_catalogue.py` from the
 * six supplier allergen exports dated 2026-09-02. Do not hand-edit
 * `windmill-catalogue.generated.json` — re-run the script instead.
 *
 * Products are scoped to Windmill Village Hotel through their site departments.
 * `Product` itself is organization-scoped, so this is department-level scoping, not
 * hard per-site isolation.
 */

export const demoSession: SessionInfo = {
  member_id: "22222222-2222-4222-8222-222222222222",
  organization_id: DEMO_ORGANIZATION_ID,
  permissions: [
    "products.view",
    "products.create",
    "products.edit",
    "allergens.edit",
    "shelf_life.edit",
    "shelf_life.approve",
    "roles.manage",
    "members.manage_lower_roles",
    "members.manage",
    "departments.view",
    "departments.manage",
    "labels.print",
    "labels.reprint",
    "printers.manage",
    "buffet.manage",
    "tenancy.manage",
  ],
  assurance_level: "aal2",
  inactivity_timeout_minutes: 5,
  member_display_name: "Alex Morgan",
  physical_printing_enabled: false,
};

const SITE_ID = catalogue.site.id;
const SITE_NAME = catalogue.site.name;

const demoCategories: Category[] = catalogue.categories.map((item) => ({
  id: item.id,
  organization_id: DEMO_ORGANIZATION_ID,
  name: item.name,
  sort_order: item.sort_order,
  active: item.active,
  version: item.version,
}));

const demoAllergens: AllergenDefinition[] = catalogue.allergens.map((item) => ({
  id: item.id,
  code: item.code,
  name: item.name,
  parent_id: item.parent_id,
  regulated: item.regulated,
  sort_order: item.sort_order,
}));

const allergenByCode = new Map(demoAllergens.map((item) => [item.code, item]));

export const demoReferences: ReferenceData = {
  categories: demoCategories,
  allergens: demoAllergens,
  label_types: catalogue.label_types,
};

const departmentSeeds = [
  ["dept-kitchen", "Kitchen", "kitchen", 10, true],
  ["dept-bakery", "Bakery", "bakery", 20, true],
  ["dept-pastry", "Pastry", "pastry", 30, true],
  ["dept-butchery", "Butchery", "butchery", 40, true],
  ["dept-cold", "Cold Kitchen", "cold-kitchen", 50, true],
] as const;

export const demoDepartments: Department[] = departmentSeeds.map(
  ([id, name, code, sortOrder, active]) => ({
    id: `site-${id}`,
    organization_id: DEMO_ORGANIZATION_ID,
    site_id: SITE_ID,
    site_name: SITE_NAME,
    department_id: id,
    name,
    code,
    sort_order: sortOrder,
    active,
    version: 1,
    // Every imported supplier line lands in the main kitchen; departments are
    // refined by hand from there.
    product_count: id === "dept-kitchen" ? catalogue.products.length : 0,
  }),
);

const kitchen = demoDepartments[0];

const shelfLifeRules: (productId: string) => ShelfLifeRule[] = (productId) =>
  catalogue.shelf_lives.map((rule) => ({
    id: `${productId}-${rule.storage_state}`,
    site_id: SITE_ID,
    label_type_id: rule.label_type_id,
    label_type_code: rule.label_type_code,
    label_type_name: rule.label_type_name,
    storage_state: rule.storage_state as StorageState,
    duration_value: rule.duration_value,
    duration_unit: "days",
    calculation_method: "local_end_of_day",
    date_mark_type: "use_by",
    approval_status: "approved",
    valid_from: null,
    valid_until: null,
    version: 1,
  }));

function allergensFor(codes: string[], relation: ProductAllergen["relation"], evidence: string): ProductAllergen[] {
  return codes.flatMap((code) => {
    const definition = allergenByCode.get(code);
    if (!definition) return [];
    return [{
      allergen_id: definition.id,
      code: definition.code,
      name: definition.name,
      relation,
      evidence,
    }];
  });
}

export const demoProductDetails: ProductDetail[] = catalogue.products.map((item) => {
  const supplierEvidence = `Supplier declaration · ${item.suppliers.join(" + ")}`;
  return {
    product: {
      id: item.id,
      organization_id: DEMO_ORGANIZATION_ID,
      site_id: SITE_ID,
      name: item.name,
      category_id: item.category_id,
      storage_instructions: null,
      unit_of_measure: item.unit_of_measure as UnitOfMeasure,
      active: item.active,
      version: 1,
    },
    departments: [
      {
        site_department_id: kitchen.id,
        department_id: kitchen.department_id,
        site_id: kitchen.site_id,
        name: kitchen.name,
        active: true,
      },
    ],
    allergens: [
      ...allergensFor(item.contains, "contains", supplierEvidence),
      ...allergensFor(item.may_contain, "may_contain", supplierEvidence),
    ],
    shelf_life_rules: shelfLifeRules(item.id),
  };
});

export const demoIdentityAdministration: IdentityAdministration = {
  members: [
    { id: demoSession.member_id!, organization_id: DEMO_ORGANIZATION_ID, display_name: "Alex Morgan", employee_reference: "A001", member_type: "named", active: true, version: 1, protected: true },
    { id: "member-2", organization_id: DEMO_ORGANIZATION_ID, display_name: "Sam Taylor", employee_reference: "S014", member_type: "named", active: true, version: 1, protected: false },
    { id: "member-3", organization_id: DEMO_ORGANIZATION_ID, display_name: "Jamie Lee", employee_reference: "J021", member_type: "pin_only", active: true, version: 1, protected: false },
  ],
  permissions: demoSession.permissions.map((code, index) => ({
    id: `permission-${index + 1}`,
    code,
    description: code.replaceAll("_", " ").replace(".", " · "),
    risk_level: code.includes("approve") || code.includes("manage") ? 100 : 40,
  })),
  roles: [
    { id: "role-admin", organization_id: DEMO_ORGANIZATION_ID, name: "Platform Administrator", protected_level: 100, permissions: demoSession.permissions, active: true, version: 1 },
    { id: "role-chef", organization_id: DEMO_ORGANIZATION_ID, name: "Head Chef", protected_level: 70, permissions: ["products.view", "products.create", "products.edit", "allergens.edit", "shelf_life.edit", "labels.print"], active: true, version: 1 },
  ],
  assignments: [
    { id: "assignment-admin", organization_id: DEMO_ORGANIZATION_ID, member_id: demoSession.member_id!, role_id: "role-admin", created_by: demoSession.member_id, scope_type: "organization", site_id: null },
    { id: "assignment-chef", organization_id: DEMO_ORGANIZATION_ID, member_id: "member-2", role_id: "role-chef", created_by: demoSession.member_id, scope_type: "organization", site_id: null },
  ],
  auth_linked_member_ids: [demoSession.member_id!, "member-2"],
};
